import { Box, Card, CardContent, Tooltip, Typography, alpha } from "@mui/material"
import * as hooks from "lib/hooks"
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material"
import { createContext } from "react"

export const FormCardContext = createContext({ hideLabel: false })

export function FormCard({
	children,
	label,
	toolTip,
	variant = "default",
}: {
	children: React.ReactNode
	label: string
	toolTip?: string
	variant?: "default" | "nested"
}): React.JSX.Element {
	const { theme } = hooks.UseTheme()
	const isNested = variant === "nested"

	const cardStyle = {
		borderRadius: isNested ? 0 : 1,
		border: isNested ? "none" : `1px solid ${alpha(theme.palette.divider, 0.08)}`,
		background: isNested ? "transparent" : alpha(theme.palette.background.paper, 0.8),
		backdropFilter: isNested ? "none" : "blur(15px)",
		boxShadow: isNested ? "none" : undefined,
		overflow: "visible",
		position: "relative",
		transition: "all 0.25s cubic-bezier(0.17, 0.67, 0.83, 0.67)",
		borderLeft: isNested ? "none" : `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
		borderTop: isNested ? "none" : `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
	}

	const labelStyle = {
		fontWeight: 600,
		fontSize: isNested ? "0.8rem" : "0.9rem",
		background: isNested
			? "none"
			: `linear-gradient(45deg, ${theme.palette.text.primary}, ${alpha(
				theme.palette.text.primary,
				0.8
			)})`,
		color: isNested ? theme.palette.text.primary : "transparent",
		backgroundClip: "text",
		textFillColor: "transparent",
		WebkitBackgroundClip: "text",
		WebkitTextFillColor: isNested ? "inherit" : "transparent",
		letterSpacing: "0.01em",
	}

	const tooltipStyles = {
		tooltip: {
			bgcolor: alpha(theme.palette.primary.dark, 0.9),
			backdropFilter: "blur(8px)",
			borderRadius: 2,
			boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.2)}`,
			p: 1.5,
			fontWeight: 500,
			border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
		},
		arrow: {
			color: alpha(theme.palette.primary.dark, 0.9),
		},
	}

	return (
		<FormCardContext.Provider value={{ hideLabel: true }}>
			<Card sx={{ ...cardStyle, p: 0, width: "100%", flex: 1, display: "flex", flexDirection: "column" }} elevation={isNested ? 0 : undefined}>
				<CardContent
					sx={{
						p: isNested ? 0 : 2,
						"&:last-child": { pb: isNested ? 0 : 2 },
						display: "flex",
						flexDirection: "column",
						flex: 1,
					}}
				>
					<Box
						display="flex"
						alignItems="center"
						mb={isNested ? 0.5 : 1.5} // Reduce bottom margin between label and content
					>
						<Typography sx={labelStyle}>
							{label}
						</Typography>
						{toolTip && (
							<Tooltip
								title={toolTip}
								placement="top"
								arrow
								componentsProps={{
									tooltip: { sx: tooltipStyles.tooltip },
									arrow: { sx: tooltipStyles.arrow },
								}}
							>
								<InfoOutlinedIcon
									sx={{
										ml: 1,
										fontSize: isNested ? 16 : 18,
										color: theme.palette.primary.main,
										opacity: 0.8,
										cursor: "pointer",
									}}
								/>
							</Tooltip>
						)}
					</Box>

					{children}
				</CardContent>
			</Card>

		</FormCardContext.Provider>
	)
}
